import React, { useState } from "react";
import { DrawerLayout } from "../../../../ui/layouts/DrawerLayout";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { Checkbox } from "../../../../ui/components/Checkbox";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import {
  FeatherX,
  FeatherInfo,
  FeatherPaperclip,
  FeatherDownload,
  FeatherFileText,
  FeatherCheck,
  FeatherEyeOff,
} from "@subframe/core";
import { EmailIntakeQueueListEntity } from "../../domain/entities/EmailIntakeQueueEntity";
import { format } from "date-fns";

interface EmailDetailsTrayProps {
  email: EmailIntakeQueueListEntity | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateStatus: (ids: number[], status: "pushed" | "ignore") => Promise<void>;
  onDownloadAttachment: (url: string, filename: string) => void;
}

export const EmailDetailsTray: React.FC<EmailDetailsTrayProps> = ({
  email,
  open,
  onOpenChange,
  onUpdateStatus,
  onDownloadAttachment,
}) => {
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  if (!email) return null;

  const handleClose = () => {
    onOpenChange(false);
    setIsUploaded(false);
  };

  const handleMarkAsCompleted = async () => {
    if (!isUploaded && email.status === "review") {
      // Show validation message or prevent action
      return;
    }

    setIsUpdating(true);
    try {
      await onUpdateStatus([email.id], "pushed");
      handleClose();
    } catch (error) {
      console.error("Failed to mark as completed:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleIgnore = async () => {
    setIsUpdating(true);
    try {
      await onUpdateStatus([email.id], "ignore");
      handleClose();
    } catch (error) {
      console.error("Failed to ignore email:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDownloadAll = () => {
    email.attachments.forEach((url, index) => {
      const filename = getFileName(url) || `attachment-${index + 1}`;
      onDownloadAttachment(url, filename);
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "review":
        return <Badge variant="warning">Review Required</Badge>;
      case "pushed":
        return <Badge variant="success">Completed</Badge>;
      case "ignore":
        return <Badge variant="neutral">Ignored</Badge>;
      default:
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category: string | null) => {
    if (!category) return null;

    const categoryLabels: Record<string, string> = {
      ap: "AP",
      ar: "AR",
      bank: "Bank",
      general: "General inbox",
    };

    const variant = category === "general" ? "warning" : "neutral";
    return (
      <Badge variant={variant as any}>
        {categoryLabels[category] || category}
      </Badge>
    );
  };

  const parseEmailAddress = (emailStr: string) => {
    const match = emailStr.match(/^(.+?)\s*<(.+)>$|^(.+)$/);
    if (match) {
      return {
        name: match[1] || match[3]?.split("@")[0] || "",
        email: match[2] || match[3] || emailStr,
      };
    }
    return { name: emailStr.split("@")[0] || "", email: emailStr };
  };

  const senderInfo = parseEmailAddress(email.from_email);
  const toEmail = email.to_emails?.[0] || "N/A";
  const receivedDate = email.received_at || email.created_at;

  const getFileIcon = (url: string) => {
    const ext = url.split(".").pop()?.toLowerCase();
    if (ext === "pdf") return <FeatherFileText />;
    if (["xlsx", "xls", "csv"].includes(ext || "")) return <FeatherFileText />;
    return <FeatherFileText />;
  };

  const getFileName = (url: string) => {
    try {
      // Remove query parameters if present (for signed URLs)
      const urlWithoutParams = url.split("?")[0];
      // Extract filename from path (last segment after /)
      const filename = urlWithoutParams.split("/").pop() || "attachment";
      return filename;
    } catch {
      return "attachment";
    }
  };

  return (
    <DrawerLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex h-full w-144 flex-col items-start bg-default-background">
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border px-6 py-6">
          <div className="flex w-full items-start justify-between">
            <div className="flex flex-col items-start gap-2">
              <span className="text-heading-2 font-heading-2 text-default-font">
                {email.subject || "No Subject"}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  {senderInfo.name || "Unknown"}
                </span>
                <span className="text-caption font-caption text-neutral-400">
                  •
                </span>
                <span className="text-body font-body text-subtext-color">
                  {senderInfo.email}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-caption font-caption text-neutral-500">
                  {formatDate(receivedDate)}
                </span>
                {getStatusBadge(email.status)}
              </div>
            </div>
            <IconButton
              variant="neutral-tertiary"
              icon={<FeatherX />}
              onClick={handleClose}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 px-6 py-6 overflow-y-auto">
          {/* Info Banner */}
          {email.status === "review" && (
            <div className="flex w-full items-start gap-3 rounded-md border border-solid border-neutral-300 bg-neutral-50 px-4 py-4">
              <FeatherInfo className="text-body font-body text-neutral-600" />
              <div className="flex flex-col items-start gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  This email could not be auto-processed
                </span>
                <span className="text-body font-body text-subtext-color">
                  Please download the attachment, upload it manually in the
                  relevant Data tab (AP / AR / Bank), and then mark this email
                  as completed.
                </span>
              </div>
            </div>
          )}

          {/* Email Details */}
          <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Email Details
            </span>
            <div className="flex w-full flex-col items-start gap-2">
              <div className="flex w-full items-center justify-between">
                <span className="text-caption font-caption text-subtext-color">
                  From
                </span>
                <span className="text-body font-body text-default-font">
                  {senderInfo.name} ({senderInfo.email})
                </span>
              </div>
              <div className="flex w-full items-center justify-between">
                <span className="text-caption font-caption text-subtext-color">
                  To
                </span>
                <span className="text-body font-body text-default-font">
                  {toEmail}
                </span>
              </div>
              {email.subdomain && (
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Client
                  </span>
                  <Badge variant="neutral">{email.subdomain}</Badge>
                </div>
              )}
              {email.category && (
                <div className="flex w-full items-center justify-between">
                  <span className="text-caption font-caption text-subtext-color">
                    Reason for Review
                  </span>
                  {getCategoryBadge(email.category)}
                </div>
              )}
            </div>
          </div>

          {/* Attachments */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background px-4 py-4">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <FeatherPaperclip className="text-body font-body text-default-font" />
                  <span className="text-body-bold font-body-bold text-default-font">
                    Attachments
                  </span>
                  <Badge variant="neutral">{email.attachments.length}</Badge>
                </div>
                <Button
                  variant="neutral-secondary"
                  size="small"
                  icon={<FeatherDownload />}
                  onClick={handleDownloadAll}
                >
                  Download All
                </Button>
              </div>
              <div className="flex w-full flex-col items-start gap-2">
                {email.attachments.map((url, index) => {
                  const filename = getFileName(url);

                  return (
                    <div
                      key={index}
                      className="flex w-full items-center gap-3 rounded-md border border-solid border-neutral-border bg-neutral-50 px-3 py-3"
                    >
                      <IconWithBackground
                        variant="neutral"
                        icon={getFileIcon(url)}
                      />
                      <div className="flex grow shrink-0 basis-0 flex-col items-start gap-0.5">
                        <span className="text-body-bold font-body-bold text-default-font">
                          {filename}
                        </span>
                        {/* <span className="text-caption font-caption text-subtext-color">
                          {fileType}
                        </span> */}
                      </div>
                      <IconButton
                        variant="neutral-tertiary"
                        icon={<FeatherDownload />}
                        onClick={() => onDownloadAttachment(url, filename)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {email.status === "review" && (
          <div className="flex w-full flex-col items-start gap-4 border-t border-solid border-neutral-border bg-neutral-50 px-6 py-4">
            <div className="flex w-full items-center gap-2">
              <Checkbox
                label=""
                checked={isUploaded}
                onCheckedChange={setIsUploaded}
              />
              <span className="text-body font-body text-default-font">
                I have uploaded this document in the Data tab
              </span>
            </div>
            <div className="flex w-full flex-col items-start gap-2">
              <div className="flex w-full items-center gap-2">
                <Button
                  className="h-auto w-auto flex-none"
                  disabled={!isUploaded || isUpdating}
                  icon={<FeatherCheck />}
                  onClick={handleMarkAsCompleted}
                >
                  Mark as Completed
                </Button>
                <Button
                  className="h-auto w-auto flex-none"
                  variant="neutral-secondary"
                  icon={<FeatherEyeOff />}
                  onClick={handleIgnore}
                  disabled={isUpdating}
                >
                  Ignore
                </Button>
              </div>
              <span className="text-caption font-caption text-subtext-color">
                Mark this email as completed after uploading the document in the
                Data tab.
              </span>
            </div>
          </div>
        )}
      </div>
    </DrawerLayout>
  );
};
